import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStyles } from './styles';
import { MdAdd } from 'react-icons/md';
import { IconButton, Tooltip } from '@mui/material';

import { Action } from 'helpers';
import SearchWithFilters from 'components/SearchWithFilters';
import { tableStyles } from 'components/Tables/TableCells/styles';
import { debounce } from 'GeminiViewerComponent/helpers/lodashUtils';
import PopupAction from 'components/Misc/PopupActions/components/PopupAction';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { makeButtonStyles } from '../../../../GeminiViewerComponent/components/styles';
import { ResizableTable } from 'GeminiViewerComponent/components/Tables/ResizableTable';
import { EnhancedTableToolbar } from 'GeminiViewerComponent/components/EnhancedTableToolbar';
import { closePanel, openPanel, setEditObjectId } from 'features/common/formSlice';
import {
    resizableLabelHeader,
    resizableLabelCell,
    resizableActionCell,
    resizableCheckCell,
    resizableCheckHeaderCell,
} from 'components';

function GroupTable({ groups, pageInfo, groupPayload, setGroupPayload, sortDirection, sortBy, handleSort }) {
    const dispatch = useDispatch();
    const theme = useSelector(selectDefaultTheme);
    const buttonStyles = makeButtonStyles(theme);
    const classes = useStyles();
    const tableClasses = tableStyles();
    const [selected, setSelected] = useState([]);
    const [selectedAll, setSelectedAll] = useState(false);

    const loadedRowsMap = useSelector((state) => state.groups.loadedGroupsMap);

    const headerHeight = 40;
    const rowHeight = 60;

    const isSelected = (id) => selected.indexOf(id) !== -1;

    const prevSelectedAll = usePrevious(selectedAll);
    if (prevSelectedAll && selectedAll) {
        if (selected.length !== groups.length) {
            setSelected(groups.map((row) => row.group_id));
            setSelectedAll(true);
        }
    }

    const handleClick = (event, group_id) => {
        event.stopPropagation();
        const selectedIndex = selected.indexOf(group_id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, group_id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
        setSelectedAll(newSelected.length === groups.length);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelectedIds = groups.map((group) => group.group_id);
            setSelected(newSelectedIds);
            setSelectedAll(true);
            return;
        }
        setSelected([]);
        setSelectedAll(false);
    };

    function usePrevious(value) {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    }

    const handleRowClick = async ({ rowData }) => {
        await dispatch(
            closePanel({
                formKey: 'groupForm',
            })
        );
        if (rowData?.group_id) {
            await dispatch(
                setEditObjectId({
                    formKey: `groupForm`,
                    object_id: rowData?.group_id,
                })
            );
            dispatch(
                openPanel({
                    formKey: `groupForm`,
                    formAction: 'Edit',
                })
            );
        }
    };

    const columns = [
        {
            accessorKey: 'group_id',
            size: 50,
            minSize: 50,
            enableResizing: false,
            label: 'Select',
            padding: 'checkbox',
            cellLevel: 'group',
            numSelected: selected.length,
            rowCount: groups.length,
            handleClick: handleClick,
            handleSelectAllClick: handleSelectAllClick,
            isSelected: isSelected,
            header: (info) => resizableCheckHeaderCell(null, info?.header?.column?.columnDef),
            cell: (info) => resizableCheckCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'group_id',
            size: 50,
            minSize: 50,
            enableResizing: false,
            padding: 'none',
            label: '',
            actionCellLevel: 'group',
            targetPopup: 'groupManagement',
            formKey: 'groupForm',
            dispatch: dispatch,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableActionCell({ rowData: info?.row?.original }, info?.column?.columnDef),
        },
        {
            accessorKey: 'display_name',
            flexGrow: 1,
            size: 250,
            minSize: 250,
            label: 'Group',
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
        {
            accessorKey: 'user_count',
            flexGrow: 1,
            size: 100,
            minSize: 100,
            label: 'N° Users',
            enableResizing: false,
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
        {
            accessorKey: 'asset_count',
            flexGrow: 1,
            size: 100,
            minSize: 100,
            label: 'N° Assets',
            enableResizing: false,
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
        {
            accessorKey: 'tag_count',
            flexGrow: 1,
            size: 100,
            minSize: 100,
            label: 'N° Tags',
            enableResizing: false,
            padding: 'normal',
            sortDirection: sortDirection,
            sortBy: sortBy,
            handleSort: handleSort,
            header: (info) => resizableLabelHeader(null, info?.header?.column?.columnDef),
            cell: (info) => resizableLabelCell(info?.row?.original, info?.column?.columnDef),
        },
    ];

    // eslint-disable-next-line no-unused-vars
    function loadMoreRows({ startIndex, stopIndex }) {}

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((searchString) => {
            setGroupPayload({ ...groupPayload, searchString: searchString });
        }, 1000),
        [groupPayload]
    );

    const handleCreateNew = () => {
        dispatch(
            openPanel({
                formKey: 'groupForm',
                formAction: 'Create',
                clearEditId: true,
            })
        );
    };

    return (
        <div className={classes.root}>
            <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
                <Tooltip title="Add New Group">
                    <IconButton onClick={handleCreateNew} size="large" className={buttonStyles.contentAddButton}>
                        <MdAdd className="react-icon" />
                    </IconButton>
                </Tooltip>
                <div style={{ flexGrow: 1 }}></div>
                <SearchWithFilters filter={false} onChange={(evt) => debouncedSearch(evt.target.value)} />
            </div>
            {selected.length > 0 && (
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    toolTipTitle={'Delete'}
                    totalCount={pageInfo.TotalCount}
                >
                    <PopupAction
                        action={Action.DeleteArray}
                        object={{
                            groupIds: selected,
                            setGroupIds: setSelected,
                        }}
                        level={'group'}
                        showLabel={false}
                    />
                </EnhancedTableToolbar>
            )}
            <div
                style={{
                    height: `calc(100vh - ${selected.length > 0 ? '345px' : '280px'})`,
                    minWidth: '500px',
                }}
            >
                <ResizableTable
                    initialRows={groups}
                    allRows={groups}
                    totalRowCount={pageInfo.TotalCount}
                    loadedRowsMap={loadedRowsMap}
                    loadMoreRows={loadMoreRows}
                    columns={columns}
                    cellClassName={tableClasses.flexContainer}
                    className={tableClasses.table}
                    rowClassName={tableClasses.flexContainer}
                    headerHeight={headerHeight}
                    rowHeight={rowHeight}
                    onRowClick={handleRowClick}
                />
            </div>
        </div>
    );
}

export { GroupTable };
